/**
 * useStudentAccountState — les gestes de « Mon compte », et ses deux écritures.
 *
 * Les gestes de `MyAccountScreen.js` et de ses pièces : la croix (`goBack`), « Paramètres »
 * (`settings`), les écrans du jeu (`skill-set`, `ranking`, `shop`), « Valide ton adresse email »
 * (`validate-email`), « Complète ton profil » (la page Profil des paramètres — l'ancienne carte
 * ouvrait l'onglet d'index 1, devenu Adresse quand l'onglet a été inséré), « Se déconnecter »
 * (`useLogoutAdapter`, le port de `useLogout`).
 *
 * L'avatar (`CustomizableAvatar.js`) : « Personnaliser » ouvre l'éditeur de l'ancien code (`avatar`)
 * sur mobile, l'avis « sur l'application mobile » sur le web ; « Réinitialiser » confirme puis
 * envoie `updateAvatarSelected` à vide ; « Générer un avatar IA » ouvre la feuille de la génération
 * (`useStudentAvatarAI`, dont l'issue passe par le même avis de la page).
 *
 * Le titre (`ChooseTitleModal.js`) : `updateTitleSkillSet`, la feuille refermée au succès. Un échec
 * de l'un ou de l'autre se dit dans l'avis de la page, plus dans une boîte de l'ancien code.
 */
import { useNavigation } from "@react-navigation/native"
import Constants from "expo-constants"
import { useCallback, useMemo, useReducer } from "react"
import { Platform } from "react-native"

import { authService } from "../../../../../../data/services/authService"
import { type StudentAccountNotice } from "../../../../../../design-system/pages/StudentAccountPage/StudentAccountPage"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useLogoutAdapter } from "../../../hooks/useLogoutAdapter"
import { type StudentAccountViewModel } from "../../../../view-models/student/Account/studentAccountViewModel"
import { initialStudentAccountUiState, studentAccountUiReducer, type AvatarMenuStep } from "./studentAccountUiState"

type StackNavigation = { push: (route: string, params?: object) => void; goBack: () => void }

const AVATAR_FAILED = "Ton avatar n'a pas pu être mis à jour. Réessaie dans un instant."
const TITLE_FAILED = "Ton titre n'a pas pu être changé. Réessaie dans un instant."

/** La page Profil des paramètres (l'ordre des onglets d'aujourd'hui : Général, Adresse, Profil, Réglages). */
const PROFILE_TAB = 2

/** La génération par IA, vue d'ici : l'ouvrir, et son avis. */
type AvatarAIEntry = { open: () => void; notice: StudentAccountNotice | null; dismissNotice: () => void }

export function useStudentAccountState(
    vm: Pick<StudentAccountViewModel, "canOpenGame" | "needsEmailValidation" | "canCompleteProfile" | "titleOptions">,
    avatarAI?: AvatarAIEntry
) {
    const navigation = useNavigation() as unknown as StackNavigation
    const [ui, dispatchUi] = useReducer(studentAccountUiReducer, initialStudentAccountUiState)
    const { logout, loggingOut } = useLogoutAdapter()

    const setAvatarStep = useCallback((step: AvatarMenuStep) => dispatchUi({ type: "SET_AVATAR_STEP", step }), [])

    const { start: startAvatar } = useApiMutation({
        service: authService.updateAvatarSelected,
        voice: "student",
        fallback: AVATAR_FAILED,
        onError: (message) => dispatchUi({ type: "FAILED", message }),
    })

    const { start: startTitle } = useApiMutation({
        service: authService.updateTitleSkillSet,
        voice: "student",
        fallback: TITLE_FAILED,
        onSuccess: () => dispatchUi({ type: "TITLE_SAVED" }),
        onError: (message) => dispatchUi({ type: "FAILED", message }),
    })

    const push = useCallback((route: string, params?: object) => navigation.push(route, params), [navigation])

    const avatarMenu = useMemo(
        () => ({
            step: ui.avatarStep,
            onClose: () => setAvatarStep("closed"),
            onCustomize: () => {
                if (Platform.OS === "web") {
                    setAvatarStep("mobile-only")
                    return
                }
                setAvatarStep("closed")
                push("avatar")
            },
            onGenerateAI:
                avatarAI != null
                    ? () => {
                          setAvatarStep("closed")
                          avatarAI.open()
                      }
                    : undefined,
            onAskReset: () => setAvatarStep("confirm-reset"),
            onConfirmReset: () => {
                setAvatarStep("closed")
                startAvatar({}, { avatar: null, avatar_characters: null })
            },
        }),
        // `open` seul : l'objet de la génération change à chaque rendu, son geste non.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ui.avatarStep, setAvatarStep, push, startAvatar, avatarAI?.open]
    )

    const titleModal = useMemo(
        () => ({
            visible: ui.titleOpen,
            onClose: () => dispatchUi({ type: "CLOSE_TITLE" }),
            options: vm.titleOptions,
            savingId: ui.savingTitleId,
            onSelect: (id: string | number) => {
                if (ui.savingTitleId != null) return
                dispatchUi({ type: "START_TITLE", id })
                startTitle({}, { skill_set_id: id })
            },
        }),
        [ui.titleOpen, ui.savingTitleId, vm.titleOptions, startTitle]
    )

    return {
        onClose: () => navigation.goBack(),
        onOpenSettings: () => push("settings"),
        onValidateEmail: vm.needsEmailValidation ? () => push("validate-email") : undefined,
        onCompleteProfile: vm.canCompleteProfile ? () => push("settings", { initialTab: PROFILE_TAB }) : undefined,
        onChooseTitle: () => dispatchUi({ type: "OPEN_TITLE" }),
        onOpenQuests: vm.canOpenGame ? () => push("skill-set") : undefined,
        onOpenRanking: vm.canOpenGame ? () => push("ranking") : undefined,
        onOpenShop: vm.canOpenGame ? () => push("shop") : undefined,
        onEditAvatar: () => setAvatarStep("menu"),
        avatarMenu,
        titleModal,
        // Un geste refusé passe avant l'issue d'une génération : c'est la réponse au dernier geste.
        notice: ui.notice != null ? { message: ui.notice, tone: "danger" as const } : (avatarAI?.notice ?? null),
        onDismissNotice: () => {
            if (ui.notice != null) dispatchUi({ type: "DISMISS_NOTICE" })
            else avatarAI?.dismissNotice()
        },
        onSwitchAccount: logout,
        switchingAccount: loggingOut,
        version: Constants.expoConfig?.version,
    }
}
